import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

function create(opts={}){
  const ingestion=opts.ingestion||g.ZooInvestRuntimeProviderIngestionBridge;
  if(!ingestion||typeof ingestion.create!=='function') throw Error('INGESTION_BRIDGE_REQUIRED');

  const bridge=ingestion.create({gateway:opts.gateway||g.ZooInvestRuntimeDataContractGateway});
  const adapters={};

  function registerAdapter(key,adapter){
    if(!adapter||!adapter.name||typeof adapter.fetch!=='function') throw Error('VALID_ADAPTER_REQUIRED');
    adapters[key]=adapter;
    bridge.register(adapter.name,adapter);
    return true;
  }

  async function loadCompanyResearchData(req={}){
    const symbol=String(req.symbol||'').trim().toUpperCase();
    if(!symbol) throw Error('SYMBOL_REQUIRED');

    const requests=[];
    if(req.cik && adapters.sec){
      requests.push({provider:adapters.sec.name,dataset:'sec-companyfacts',symbol,cik:req.cik});
    }
    if(adapters.market){
      requests.push({provider:adapters.market.name,dataset:'market-daily',symbol,outputsize:req.outputsize||'compact'});
    }

    const raw=[];
    for(const r of requests){
      raw.push(await bridge.ingest(r));
    }

    const byDataset={};
    for(const item of raw){
      if(item&&item.envelope&&item.envelope.dataset){
        byDataset[item.envelope.dataset]=item;
      }
    }

    const sec=byDataset['sec-companyfacts']||null;
    const market=byDataset['market-daily']||null;

    const combinedStatus=
      raw.some(x=>x.status==='BLOCK')?'BLOCK':
      raw.some(x=>x.status==='REVIEW')?'REVIEW':'READY';

    return Object.freeze({
      version:VERSION,
      symbol,
      status:combinedStatus,
      researchInput:Object.freeze({
        fundamentals:sec?sec.envelope.payload:null,
        fundamentalsMeta:sec?Object.freeze({
          state:sec.envelope.state,
          asOf:sec.envelope.asOf,
          provenance:sec.envelope.provenance
        }):null,
        marketDaily:market?market.envelope.payload:null,
        marketMeta:market?Object.freeze({
          state:market.envelope.state,
          asOf:market.envelope.asOf,
          provenance:market.envelope.provenance
        }):null
      }),
      providerResults:Object.freeze(raw)
    });
  }

  return Object.freeze({VERSION,registerAdapter,loadCompanyResearchData});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});

  const gateway={
    gate:e=>{
      if(!e.provider||!e.dataset) return {status:'BLOCK',envelope:e};
      if(e.state==='ERROR'||e.state==='MISSING'||e.state==='UNVERIFIED'||e.state==='STALE')
        return {status:'REVIEW',envelope:e};
      return {status:'READY',envelope:e};
    },
    combine:envs=>({status:'READY',results:envs})
  };

  const ingestion={
    create:()=>({
      providers:new Map(),
      register(name,adapter){this.providers.set(name,adapter);},
      async ingest(req){
        const a=this.providers.get(req.provider);
        if(!a) return gateway.gate({provider:req.provider,dataset:req.dataset,state:'ERROR',error:'PROVIDER_NOT_REGISTERED'});
        try{return gateway.gate({provider:req.provider,dataset:req.dataset,...(await a.fetch(req))});}
        catch(e){return gateway.gate({provider:req.provider,dataset:req.dataset,state:'ERROR',error:String(e.message||e)});}
      }
    })
  };

  const r=create({ingestion,gateway});

  const sec={
    name:'SEC',datasets:['sec-companyfacts'],
    fetch:async req=>({
      symbol:req.symbol,asOf:'2026-08-01',state:'VERIFIED',
      provenance:{provider:'SEC'},payload:{entityName:'AAA Corp',facts:{Revenue:[{value:100}]}}
    })
  };
  const market={
    name:'ALPHA_VANTAGE',datasets:['market-daily'],
    fetch:async req=>({
      symbol:req.symbol,asOf:'2026-08-29',state:'VERIFIED',
      provenance:{provider:'Alpha Vantage'},payload:{rows:[{date:'2026-08-29',close:123.45}]}
    })
  };

  ok('sec adapter registers',r.registerAdapter('sec',sec)===true);
  ok('market adapter registers',r.registerAdapter('market',market)===true);

  const out=await r.loadCompanyResearchData({symbol:'aaa',cik:'123'});
  ok('symbol normalized',out.symbol==='AAA');
  ok('combined status ready',out.status==='READY');
  ok('fundamentals attached',out.researchInput.fundamentals.entityName==='AAA Corp');
  ok('market data attached',out.researchInput.marketDaily.rows[0].close===123.45);
  ok('fundamental metadata retained',out.researchInput.fundamentalsMeta.asOf==='2026-08-01');
  ok('market metadata retained',out.researchInput.marketMeta.asOf==='2026-08-29');
  ok('two provider results retained',out.providerResults.length===2);

  const r2=create({ingestion,gateway});
  r2.registerAdapter('market',{
    name:'ALPHA_VANTAGE',datasets:['market-daily'],
    fetch:async req=>({
      symbol:req.symbol,asOf:null,state:'MISSING',
      provenance:{provider:'Alpha Vantage'},payload:{rows:[]}
    })
  });
  const m=await r2.loadCompanyResearchData({symbol:'BBB'});
  ok('missing market degrades to review',m.status==='REVIEW');
  ok('missing fundamentals remains null',m.researchInput.fundamentals===null);
  ok('market payload remains visible',Array.isArray(m.researchInput.marketDaily.rows));

  const r3=create({ingestion,gateway});
  r3.registerAdapter('sec',{
    name:'SEC',datasets:['sec-companyfacts'],
    fetch:async()=>{throw Error('SEC_FAIL')}
  });
  const e=await r3.loadCompanyResearchData({symbol:'CCC',cik:'1'});
  ok('provider failure contained as review',e.status==='REVIEW');
  ok('failed provider result retained',e.providerResults.length===1);

  let se=false;try{await r.loadCompanyResearchData({symbol:''})}catch(e){se=e.message==='SYMBOL_REQUIRED'}
  ok('symbol required',se);

  let ae=false;try{r.registerAdapter('x',{})}catch(e){ae=e.message==='VALID_ADAPTER_REQUIRED'}
  ok('invalid adapter rejected',ae);

  let ie=false;try{create({ingestion:{}})}catch(e){ie=e.message==='INGESTION_BRIDGE_REQUIRED'}
  ok('ingestion bridge required',ie);

  const api=create({ingestion,gateway});
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestProviderToResearchRuntimeBridge=Object.freeze({VERSION,create,selfTest});
})(engineContext);